<template>
  <div class="about">
    <figure v-reveal class="about-image about-image--left reveal">
      <img
        :src="spaceImageTwo"
        alt="Black and white film portrait of an actor in a shearling-collared denim jacket"
        width="1200"
        height="1200"
      />
    </figure>

    <article v-reveal class="about-copy reveal">
      <h1 class="about-title">ABOUT US</h1>

      <p class="about-lead">
        Studio Butter is a photo-video studio, based in central south London and run by Matt
        Whitchurch and Sydney Sainté.
      </p>

      <p>
        We started this endeavor out of rebellion and a need to reclaim our right to practise our
        artform on our own terms, without anyone’s say so. When you’re a working artist, it can
        feel like you’re spending a big portion of your time waiting for opportunities to come your
        way, which can make you feel isolated and at sea. Studio Butter is the antidote to that;
        whether you’re booking a self-tape session for your next big West End gig or you’re looking
        to refresh your headshots, or you are simply in search of a creative community to join, our
        door is wide open.
      </p>

      <p>
        Our spacious studio is located in the front area of our light-soaked Victorian flat and is
        tucked away on a quiet, tranquil street, with highly accessible transport links. The space
        is versatile, inviting and fully kitted-out with high quality equipment - making it the
        ideal setting for all your professional photography and videography needs. We work with a
        mix of hand-painted canvas backdrops and paper Colourama backdrops, an array of film &amp;
        digital cameras with vintage lenses, wireless mics, plenty of continuous lighting and more.
      </p>

      <p>
        We want you, our client, to feel comfortable and supported when you pay us a visit; it
        should feel as though you have found your artistic home away from home. So you’ll notice a
        vintage, bespoke approach to how we run the place - think Moroccan rugs, mid-century modern
        furnishings and lots of soft, natural light streaming in through the bay windows. You’ll
        have access to our green room and bathroom. We will take care of the atmos and the
        amenities, so you can focus solely on your work.
      </p>

      <p>
        Oh, and we have three studio cats! Kenny, Clay &amp; Carmela will not be freely roaming the
        flat while the studio is in session, but please do let us know if you are allergic.
      </p>

      <footer class="about-signoff">
        <p class="about-signoff-line">
          We can’t wait to host you. After all, everything is better with Butter.
        </p>
        <p class="about-signature">Studio Butter.</p>
      </footer>
    </article>

    <figure v-reveal class="about-image about-image--right reveal" style="--reveal-delay: 0.2s">
      <img
        :src="spaceImageOne"
        alt="Portrait of an actor in a white ruffled-collar blouse against a warm ochre backdrop"
        width="1200"
        height="1800"
      />
    </figure>
  </div>
</template>

<script>
import spaceImageOne from "@/assets/space/space-1.jpg";
import spaceImageTwo from "@/assets/space/space-2.jpg";

export default {
  name: "TheSpace",
  data() {
    return {
      spaceImageOne,
      spaceImageTwo,
    };
  },
};
</script>

<style scoped>
/* Image | copy | image. The images stay in view (sticky) while the copy scrolls,
   and the right one sits lower so the two don't mirror each other exactly. */
.about {
  --sticky-top: calc(var(--nav-height) + 48px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 34rem) minmax(0, 1fr);
  grid-template-areas: "left copy right";
  align-items: start;
  gap: clamp(40px, 5vw, 88px);
  max-width: 1440px;
  margin: 0 auto;
  padding: 48px 40px 140px;
  background-color: var(--color-paper);
  color: var(--color-ink);
}

.about-image {
  margin: 0;
  position: sticky;
  top: var(--sticky-top);
}

.about-image img {
  display: block;
  width: 100%;
  height: auto;
}

.about-image--left {
  grid-area: left;
}

.about-image--right {
  grid-area: right;
  margin-top: 180px;
}

.about-copy {
  grid-area: copy;
  display: flex;
  flex-direction: column;
  gap: 22px;
  font-size: 17px;
  line-height: 1.75;
}

.about-title {
  font-family: "brown-sugar";
  font-size: 50px;
  line-height: 1;
  margin-bottom: 12px;
}

.about-lead {
  font-size: 22px;
  line-height: 1.5;
}

.about-signoff {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  padding-top: 28px;
  border-top: 1px solid var(--color-ink);
}

.about-signoff-line {
  font-family: "brown-sugar";
  font-size: 26px;
  line-height: 1.3;
}

.about-signature {
  font-size: 13px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

/* Tablet: both images side by side above the copy */
@media (max-width: 1099px) {
  .about {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas:
      "left right"
      "copy copy";
    align-items: end;
  }

  .about-image {
    position: static;
  }

  .about-image--right {
    margin-top: 0;
  }

  .about-copy {
    max-width: 34rem;
    justify-self: center;
    margin-top: 24px;
  }
}

/* Mobile: film portrait, copy, then the colour portrait */
@media (max-width: 599px) {
  .about {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "left"
      "copy"
      "right";
    padding: 32px 20px 96px;
    gap: 40px;
  }

  .about-copy {
    margin-top: 0;
  }

  .about-title {
    font-size: 40px;
  }

  .about-lead {
    font-size: 19px;
  }
}
</style>
